// src/pages/ForgotPassword.js

import React, { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  Alert,
  Paper,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  EmailOutlined,
  ArrowBackOutlined,
  SendOutlined,
  CheckCircleOutlined,
  AutoAwesomeOutlined,
  LockResetOutlined,
} from '@mui/icons-material';

// Animated Icon Component
function AnimatedLockIcon() {
  return (
    <motion.div
      initial={{ scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ duration: 0.8, type: "spring" }}
    >
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.2)',
        }}
      >
        <motion.div
          animate={{
            y: [0, -10, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <LockResetOutlined
            sx={{
              fontSize: 60,
              color: 'white',
            }}
          />
        </motion.div>
        
        {/* Orbiting particles */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'white',
            }}
            animate={{
              rotate: 360,
            }}
            transition={{
              duration: 3 + i,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'white',
                position: 'absolute',
                top: -50 - i * 10,
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            />
          </motion.div>
        ))}
      </Box>
    </motion.div>
  );
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState('');

  const validateEmail = () => {
    if (!email) {
      setFieldError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setFieldError('Please enter a valid email address');
      return false;
    }
    setFieldError('');
    return true;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!validateEmail()) return;
    
    setLoading(true);
    setError('');
    
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
    } catch (err) {
      // Error logging removed for security
      setError(
        err.code === 'auth/user-not-found'
          ? 'No account found with this email address'
          : 'Failed to send reset email. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'var(--bg-light)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        p: 2,
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '30%',
            width: 400,
            height: 400,
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
      </Box>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', maxWidth: 480, position: 'relative', zIndex: 1 }}
      >
        {!success ? (
          <Box
            className="glass-card"
            component="form"
            onSubmit={handleResetPassword}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Back Button */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Button
                startIcon={<ArrowBackOutlined />}
                onClick={() => navigate('/login')}
                sx={{
                  color: 'var(--text-secondary)',
                  mb: 3,
                  '&:hover': {
                    color: 'var(--primary-blue)',
                    bgcolor: 'transparent',
                  },
                }}
              >
                Back to Login
              </Button>
            </motion.div>

            {/* Icon */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <AnimatedLockIcon />
            </Box>

            {/* Title */}
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                fontWeight: 800,
                fontFamily: 'Space Grotesk, sans-serif',
                mb: 2,
              }}
            >
              Forgot Password?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: 'var(--text-secondary)',
                mb: 4,
              }}
            >
              No worries! Enter your email and we'll send you instructions to reset your password.
            </Typography>

            {/* Error Alert */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Alert
                    severity="error"
                    onClose={() => setError('')}
                    sx={{ mb: 3 }}
                  >
                    {error}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Email Field */}
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldError('');
              }}
              error={!!fieldError}
              helperText={fieldError}
              inputProps={{ maxLength: 100 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: 'var(--text-secondary)' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'var(--bg-white)',
                  border: '1px solid var(--border-light)',
                  '&:hover': {
                    borderColor: 'var(--primary-blue)',
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                  },
                  '&.Mui-focused': {
                    borderColor: 'var(--primary-blue)',
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                  },
                  '& fieldset': {
                    border: 'none',
                  },
                },
              }}
            />

            {/* Submit Button */}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                fullWidth
                type="submit"
                disabled={loading}
                endIcon={loading ? null : <SendOutlined />}
                sx={{
                  py: 2,
                  mb: 3,
                  background: 'linear-gradient(135deg, var(--primary-blue), var(--secondary-blue))',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
                  '&:hover': {
                    boxShadow: '0 6px 24px rgba(59, 130, 246, 0.3)',
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    background: 'var(--bg-gray)',
                    color: 'var(--text-secondary)',
                  },
                }}
              >
                {loading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <AutoAwesomeOutlined />
                  </motion.div>
                ) : (
                  'Send Reset Email'
                )}
              </Button>
            </motion.div>

            {/* Additional Links */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                Remember your password?{' '}
                <Link
                  to="/login"
                  style={{
                    color: 'var(--primary-blue)',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  Sign in
                </Link>
              </Typography>
            </Box>
          </Box>
        ) : (
          // Success State
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 100 }}
          >
            <Paper
              className="glass-card"
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: 4,
              }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
              >
                <CheckCircleOutlined
                  sx={{
                    fontSize: 80,
                    color: 'var(--success-green)',
                    filter: 'drop-shadow(0 0 12px rgba(34, 197, 94, 0.3))',
                    mb: 3,
                  }}
                />
              </motion.div>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                Check Your Email
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'var(--text-secondary)',
                  mb: 1,
                }}
              >
                We've sent a password reset link to:
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: 'var(--primary-blue)',
                  fontWeight: 600,
                  mb: 4,
                }}
              >
                {email}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'var(--text-secondary)',
                  mb: 4,
                }}
              >
                Didn't receive the email? Check your spam folder or{' '}
                <Box
                  component="span"
                  onClick={() => {
                    setSuccess(false);
                    setEmail('');
                  }}
                  sx={{
                    color: 'var(--primary-blue)',
                    cursor: 'pointer',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  try again
                </Box>
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{
                  py: 1.5,
                  borderColor: 'var(--primary-blue)',
                  color: 'var(--primary-blue)',
                  '&:hover': {
                    borderColor: 'var(--secondary-blue)',
                    color: 'var(--secondary-blue)',
                    bgcolor: 'transparent',
                  },
                }}
              >
                Back to Login
              </Button>
            </Paper>
          </motion.div>
        )}
      </motion.div>
    </Box>
  );
}
